import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useConfiguration } from '../../context/configurationContext';
import { denormalisedResponseEntities } from '../../util/data';
import { homepageListenerQueryParams } from '../../containers/LandingPage/LandingPage.duck';
import usePresenceMap from '../../util/usePresenceMap';
import useReviewStats from '../../util/useReviewStats';
import { rankHomeListenerCards } from '../../util/listingReviews';
import { isListingOnline } from '../../util/presence';
import { LISTENER_STATUS, resolveListenerAvailability } from '../../util/listenerAvailability';
import { BEST_LISTENER_IDS, BEST_LISTENER_COUNT } from '../../config/bestListeners';
import { listingHasRealFacePhoto } from '../../util/listingCardPhoto';
import CompactListenerCard, { topicChips } from '../CompactListenerCard/CompactListenerCard';
import NamedLink from '../NamedLink/NamedLink';
import { topicsFromListing } from '../../util/sessionTopics';
import css from './BestListeners.module.css';

/**
 * "Meet a few of our listeners" strip (Sam, Oct 25 2026): 3 real people, one tap
 * into a profile. Used on home and the dashboard. Self-fetching.
 */
const BestListeners = ({ title = 'Meet a few of our listeners', className, page = 0, trustLine, count = BEST_LISTENER_COUNT, topics = null, chipPrefer = null }) => {
  const dispatch = useDispatch();
  const config = useConfiguration();
  const me = useSelector(s => s?.user?.currentUser?.id?.uuid);
  const [listings, setListings] = useState([]);
  useEffect(() => {
    let live = true;
    dispatch((d, getState, sdk) =>
      sdk.listings
        .query({ ...homepageListenerQueryParams(config), perPage: 100, include: ['author', 'author.profileImage', 'images'] })
        .then(r => denormalisedResponseEntities(r))
    )
      .then(ls => live && setListings((ls || []).filter(l => l?.attributes?.state === 'published' || !l?.attributes?.state)))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sam Oct 28: best people only (Available → reviews → rating → completeness), photo
  // required, and rotate only within the top pool — never the whole marketplace.
  const wantTopics = Array.isArray(topics) && topics.length ? topics : null;
  // Theme landings (Oct 29): strict — only people whose listing lists one of the topics.
  const pool = listings.filter(l => (!me || l?.author?.id?.uuid !== me) && listingHasRealFacePhoto(l)
    && (!wantTopics || topicsFromListing(l).some(t => wantTopics.includes(t))));
  const ids = pool.map(l => l.id.uuid);
  const { map: presenceMap, pausedMap = {}, busyMap = {}, nextAvailableMap = {} } = usePresenceMap(ids);
  const statsMap = useReviewStats(ids);
  const avail = l =>
    resolveListenerAvailability({
      listing: l,
      online: isListingOnline(l.id.uuid, presenceMap, l),
      sessionBusy: !!busyMap[l.id.uuid],
      startNowPaused: !!pausedMap[l.id.uuid],
    }).status === LISTENER_STATUS.AVAILABLE;
  // FB bounce fix (Oct 30): theme landings — rank by on-theme fit (chip labels matching the
  // theme, then fewer listed topics = more specialised) before the rest of the ranking.
  const pref = wantTopics && Array.isArray(chipPrefer) && chipPrefer.length ? chipPrefer : null;
  const themeScore = l => {
    if (!wantTopics) return 0;
    const labels = pref ? topicChips(l, 50, pref).join(' ').toLowerCase() : '';
    const hits = pref ? pref.filter(k => labels.includes(String(k).toLowerCase())).length : 0;
    const n = topicsFromListing(l).length || 1;
    return hits * 10 + (n <= 3 ? 6 : n <= 6 ? 3 : 0) - n * 0.1;
  };
  const byTheme = arr => (wantTopics ? arr.map((l, i) => [l, i]).sort((a, b) => themeScore(b[0]) - themeScore(a[0]) || a[1] - b[1]).map(x => x[0]) : arr);
  const pinned = (wantTopics ? [] : BEST_LISTENER_IDS).map(id => pool.find(l => l.id.uuid === id)).filter(Boolean);
  const rest = rankHomeListenerCards(pool.filter(l => !pinned.includes(l)), { isAvailable: avail, statsMap });
  const top = byTheme([...pinned, ...rest]).slice(0, Math.max(count * 3, 16));
  // Hourly rotation (set after mount to avoid SSR mismatch): stable within an hour,
  // changes each hour. page N takes the Nth block so home/dashboard differ.
  const [hour, setHour] = useState(0);
  useEffect(() => { setHour(Math.floor(Date.now() / 3600000)); }, []);
  const offset = !wantTopics && top.length ? ((hour * count + page * count) % top.length) : 0;
  // Sam Oct 29 (bounce fix): every Available Now person first (pinned/rotation never
  // push them down); rotate only the rest.
  const availTop = byTheme([...pinned, ...rest].filter(avail));
  const restTop = top.filter(l => !availTop.includes(l));
  const rOff = restTop.length ? offset % restTop.length : 0;
  const shown = [...availTop, ...restTop.slice(rOff), ...restTop.slice(0, rOff)].slice(0, count);
  if (!shown.length) return null;
  return (
    <section className={[css.root, className].filter(Boolean).join(' ')} data-best-listeners="true">
      <div className={css.head}>
        <h2 className={css.title}>{title}</h2>
        <NamedLink name="SearchPage" to={wantTopics ? { search: `?topics=${wantTopics.join(',')}` } : undefined} className={css.all}>See everyone ›</NamedLink>
      </div>
      {trustLine ? (
        <div className={css.trustCard} data-home-trust-card="true">
          <div className={css.trustHead}>
            <span className={css.trustIcon} aria-hidden="true">🛡️</span>
            <div>
              <div className={css.trustTitle}>People you can trust</div>
              <div className={css.trustSub}>Every listener &amp; teacher is checked</div>
            </div>
          </div>
          <ul className={css.trustList}>
            <li>✓ Email verified</li>
            <li>✓ Photo ID + selfie</li>
            <li>✓ Guidelines before Available</li>
            <li>✓ Sessions moderated</li>
          </ul>
        </div>
      ) : null}
      <div className={css.row}>
        {shown.map(l => (
          <CompactListenerCard
            key={l.id.uuid}
            listing={l}
            online={isListingOnline(l.id.uuid, presenceMap, l)}
            sessionBusy={!!busyMap[l.id.uuid]}
            startNowPaused={!!pausedMap[l.id.uuid]}
            nextAvailableAt={nextAvailableMap[l.id.uuid] || null}
            className={css.card}
            variant="best"
            preferTopics={chipPrefer}
          />
        ))}
      </div>
    </section>
  );
};

export default BestListeners;
